import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { EmptyState } from "@tokenterminal/core/ui/messaging/emptyState/EmptyState"
import { ChainStatusFilters } from "modules/chain-status/ChainStatusFilters"
import { ChainStatusTable } from "modules/chain-status/ChainStatusTable"
import { useChainStatus } from "modules/chain-status/hooks/useChainStatus"
import { useProjectsArchiveStatus } from "modules/chain-status/hooks/useProjectsArchiveStatus"
import { useChainsList } from "modules/projects/hooks/useChainsList"
import { useMemo, useState } from "react"

export function ChainStatusPage() {
  const { data, isLoading, error } = useChainStatus()
  const { data: archiveStatusMap } = useProjectsArchiveStatus()
  const { data: { data: chainsMetadata = [] } = {} } = useChainsList()
  const [statusFilter, setStatusFilter] = useState<
    "all" | "fresh" | "stale" | "archived"
  >("all")
  const [searchQuery, setSearchQuery] = useState("")

  // Create a mapping from chain slugs to their metadata
  const chainMetaMap = useMemo(() => {
    const map = new Map()
    chainsMetadata.forEach((chain) => {
      // For chains with multiple entries (like ethereum, polkadot), prioritize the main chain entry
      // where chain_id matches project_slug over specific product entries
      if (chain.project_slug && chain.chain_id === chain.project_slug) {
        // This is the main chain entry, always use it
        map.set(chain.project_slug, chain)
      } else if (!map.has(chain.project_slug)) {
        // This is a product/sub-chain entry, only use it if we don't have a main entry yet
        map.set(chain.project_slug, chain)
      }
      // Skip product entries if we already have the main chain entry
    })
    return map
  }, [chainsMetadata])

  const filteredChains = useMemo(() => {
    if (!data?.data) return []

    return data.data.filter((chain) => {
      // Filter out chains that don't have metadata from the api-chain-metadata endpoint
      const hasMetadata = chainMetaMap.has(chain.slug)
      if (!hasMetadata) {
        return false
      }

      const archiveStatus = archiveStatusMap?.get(chain.slug)
      const isArchived = archiveStatus?.isArchived || false

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "fresh" && chain.is_fresh && !isArchived) ||
        (statusFilter === "stale" && !chain.is_fresh && !isArchived) ||
        (statusFilter === "archived" && isArchived)

      const matchesSearch =
        chain.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chain.api_id.toLowerCase().includes(searchQuery.toLowerCase())

      return matchesStatus && matchesSearch
    })
  }, [data?.data, statusFilter, searchQuery, archiveStatusMap, chainMetaMap])

  const stats = useMemo(() => {
    if (!data?.data) return { total: 0, fresh: 0, stale: 0, archived: 0 }

    return data.data.reduce(
      (acc, chain) => {
        // Only count chains that have metadata from the api-chain-metadata endpoint
        const hasMetadata = chainMetaMap.has(chain.slug)
        if (!hasMetadata) {
          return acc
        }

        const archiveStatus = archiveStatusMap?.get(chain.slug)
        const isArchived = archiveStatus?.isArchived || false

        return {
          total: acc.total + 1,
          fresh: acc.fresh + (chain.is_fresh && !isArchived ? 1 : 0),
          stale: acc.stale + (!chain.is_fresh && !isArchived ? 1 : 0),
          archived: acc.archived + (isArchived ? 1 : 0),
        }
      },
      { total: 0, fresh: 0, stale: 0, archived: 0 }
    )
  }, [data?.data, archiveStatusMap, chainMetaMap])

  if (error) {
    return (
      <div className="px-3 md:px-6 w-full max-w-[800px] mx-auto pt-6">
        <div className="flex flex-col w-full max-w-full gap-5">
          <Heading level={2} as="h1">
            Chain Status
          </Heading>
          <Card className="rounded-2xl p-6">
            <EmptyState
              headingText="Error loading chain status"
              bodyText="Failed to load chain status data. Please try again later."
            />
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="px-3 md:px-6 w-full pt-6">
      <div className="flex flex-col w-full max-w-[800px] mx-auto gap-5">
        <div>
          <Heading level={2} as="h1">
            Chain status
          </Heading>
          <p className="text-fg-secondary text-sm mt-1">
            Monitor the data freshness status of all supported chains
          </p>
        </div>
        <div className="flex flex-col gap-4">
          <ChainStatusFilters
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            stats={stats}
          />
        </div>
        <Card className="rounded-2xl p-0 overflow-x-auto">
          <div className="w-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-fg-secondary">Loading chain status...</div>
              </div>
            ) : filteredChains.length > 0 ? (
              <ChainStatusTable chains={filteredChains} />
            ) : (
              <EmptyState
                headingText="No chains found"
                bodyText="No chains match the current filters"
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
